import React from 'react';
import { Text } from '@cubeyou-team/hx-react-components';
import styled from 'styled-components';

import { getQueryPreviewGroupColor } from 'src/modules/query-builder/constants';
import {
  getNotExclusionChildOperator,
  isNotExclusionGroup,
  normalizeQueryItemForPreview,
} from '../queryPreviewHelpers';
import {
  StyledQueryGroup,
  StyledQueryGroupList,
  StyledQueryItem,
} from '../QueryPreview.styles';

const StyledSpan = styled.span`
  color: ${(props) => props.color};
`;

const getItemLabel = (
  type,
  groupId,
  queryItem,
  queryEntitiesInfos,
  conversationQueries
) => {
  if (type === 'conversation') {
    return conversationQueries &&
      conversationQueries[`${groupId}_${queryItem.id}`]
      ? conversationQueries[`${groupId}_${queryItem.id}`]
      : '';
  }

  return queryEntitiesInfos &&
    queryEntitiesInfos[`${queryItem.type}_${queryItem.id}`]
    ? queryEntitiesInfos[`${queryItem.type}_${queryItem.id}`].label
    : '';
};

const createGetList = (layout) => {
  const isHorizontal = layout === 'horizontal';

  const OperatorText = ({ color, children }) =>
    isHorizontal ? (
      <StyledSpan color={color}>{children}</StyledSpan>
    ) : (
      <Text style={{ color }}>{children}</Text>
    );

  const ItemLabel = ({ children }) =>
    isHorizontal ? (
      <StyledSpan style={{ fontSize: '14px' }}>{children}</StyledSpan>
    ) : (
      <Text as="span" size="xbody">
        {children}
      </Text>
    );

  const renderNotGroup = ({ block, index, colorGroup, children }) => {
    if (isHorizontal) {
      return (
        <React.Fragment key={block.id + index}>
          {index > 1 && <OperatorText color={colorGroup}>NOT</OperatorText>}
          {block.id !== 0 && (
            <OperatorText color={colorGroup}>{` (`}</OperatorText>
          )}
          {children}
          {block.id !== 0 && (
            <OperatorText color={colorGroup}>{`) `}</OperatorText>
          )}
        </React.Fragment>
      );
    }

    return (
      <StyledQueryGroup
        showBrackets
        key={block.id + index}
        className="group-not"
        data-query-item-id={block.id}
      >
        {index > 1 && <OperatorText color={colorGroup}>NOT</OperatorText>}
        <StyledQueryGroupList
          color={colorGroup}
          showBrackets
          className="group-not-list"
        >
          {children}
        </StyledQueryGroupList>
      </StyledQueryGroup>
    );
  };

  const renderRegularGroup = ({
    queryItem,
    index,
    colorGroup,
    parentOperator,
    maxLength,
    atLeast,
    children,
  }) => {
    if (isHorizontal) {
      return (
        <React.Fragment key={queryItem.id + index}>
          {queryItem.id !== 0 &&
            index === 1 &&
            parentOperator === 'AT_LEAST' && (
              <OperatorText
                color={colorGroup}
              >{`AtLeast_${atLeast}[`}</OperatorText>
            )}
          {queryItem.id !== 0 && index > 1 && parentOperator !== 'AT_LEAST' && (
            <OperatorText color={colorGroup}>{parentOperator}</OperatorText>
          )}
          {queryItem.id !== 0 && (
            <OperatorText color={colorGroup}>{`${
              parentOperator === 'AT_LEAST' ? ', ' : ''
            } (`}</OperatorText>
          )}
          {children}
          {queryItem.id !== 0 && (
            <OperatorText color={colorGroup}>{`) `}</OperatorText>
          )}
          {queryItem.id !== 0 &&
            index === maxLength &&
            parentOperator === 'AT_LEAST' && (
              <OperatorText color={colorGroup}>{`]`}</OperatorText>
            )}
        </React.Fragment>
      );
    }

    return (
      <StyledQueryGroup
        showBrackets={queryItem.id !== 0}
        key={queryItem.id + index}
        className="group"
        data-query-item-id={queryItem.id}
      >
        {queryItem.id !== 0 && index === 1 && parentOperator === 'AT_LEAST' && (
          <OperatorText
            color={colorGroup}
          >{`AtLeast_${atLeast}[`}</OperatorText>
        )}
        {queryItem.id !== 0 && index > 1 && parentOperator !== 'AT_LEAST' && (
          <OperatorText color={colorGroup}>{parentOperator}</OperatorText>
        )}
        <StyledQueryGroupList
          color={colorGroup}
          showBrackets={queryItem.id !== 0}
          className="group-list"
        >
          {children}
        </StyledQueryGroupList>
        {queryItem.id !== 0 &&
          index === maxLength &&
          parentOperator === 'AT_LEAST' && (
            <OperatorText color={colorGroup}>{`]`}</OperatorText>
          )}
      </StyledQueryGroup>
    );
  };

  const renderAtLeastItem = ({
    queryItem,
    index,
    colorGroup,
    parentOperator,
    maxLength,
    atLeast,
    itemLabel,
  }) => {
    const label = isHorizontal
      ? `${index > 1 ? ', ' : ''} ${itemLabel} `
      : `${itemLabel}${index !== maxLength ? ', ' : ''}`;

    if (isHorizontal) {
      return (
        <React.Fragment>
          {queryItem.id !== 0 &&
            index === 1 &&
            parentOperator === 'AT_LEAST' && (
              <OperatorText
                color={colorGroup}
              >{`AtLeast_${atLeast}[`}</OperatorText>
            )}
          <ItemLabel>{label}</ItemLabel>
          {queryItem.id !== 0 &&
            index === maxLength &&
            parentOperator === 'AT_LEAST' && (
              <OperatorText color={colorGroup}>{`]`}</OperatorText>
            )}
        </React.Fragment>
      );
    }

    return (
      <StyledQueryItem
        key={queryItem.id + index}
        className="item"
        data-query-item-id={queryItem.id}
      >
        {queryItem.id !== 0 && index === 1 && parentOperator === 'AT_LEAST' && (
          <OperatorText
            color={colorGroup}
          >{`AtLeast_${atLeast}[`}</OperatorText>
        )}
        <ItemLabel>{label}</ItemLabel>
        {queryItem.id !== 0 &&
          index === maxLength &&
          parentOperator === 'AT_LEAST' && (
            <OperatorText color={colorGroup}>{`]`}</OperatorText>
          )}
      </StyledQueryItem>
    );
  };

  const renderRegularItem = ({
    queryItem,
    index,
    colorGroup,
    parentOperator,
    itemLabel,
  }) => {
    if (isHorizontal) {
      return (
        <React.Fragment>
          {index > 1 && (
            <OperatorText color={colorGroup}>{parentOperator}</OperatorText>
          )}{' '}
          <ItemLabel>{`${itemLabel} `}</ItemLabel>
        </React.Fragment>
      );
    }

    return (
      <StyledQueryItem
        key={queryItem.id + index}
        className="item"
        data-query-item-id={queryItem.id}
      >
        {index > 1 && (
          <OperatorText color={colorGroup}>{parentOperator}</OperatorText>
        )}{' '}
        <ItemLabel>{itemLabel}</ItemLabel>
      </StyledQueryItem>
    );
  };

  return function getList(
    type,
    completeQuery,
    groupId,
    queryItem,
    queryEntitiesInfos,
    conversationQueries,
    parentOperator,
    index = 0,
    maxLength,
    atLeast = 0
  ) {
    const colorGroup = getQueryPreviewGroupColor(completeQuery, groupId);

    if (queryItem.type === 'block') {
      const block = normalizeQueryItemForPreview(queryItem);

      if (isNotExclusionGroup(block.operator)) {
        return renderNotGroup({
          block,
          index,
          colorGroup,
          children: (block.arguments_list || []).map((item, i) => (
            <React.Fragment key={block.id + index + i}>
              {getList(
                type,
                completeQuery,
                block.id,
                item,
                queryEntitiesInfos,
                conversationQueries,
                getNotExclusionChildOperator(block.operator),
                i + 1,
                block.arguments_list.length
              )}
            </React.Fragment>
          )),
        });
      }

      return renderRegularGroup({
        queryItem,
        index,
        colorGroup,
        parentOperator,
        maxLength,
        atLeast,
        children: queryItem.arguments_list.map((item, i) => (
          <React.Fragment key={queryItem.id + index + i}>
            {getList(
              type,
              completeQuery,
              queryItem.id,
              item,
              queryEntitiesInfos,
              conversationQueries,
              queryItem.operator,
              i + 1,
              queryItem.arguments_list.length,
              queryItem.operator === 'AT_LEAST' && queryItem.at_least
            )}
          </React.Fragment>
        )),
      });
    }

    const itemLabel = getItemLabel(
      type,
      groupId,
      queryItem,
      queryEntitiesInfos,
      conversationQueries
    );

    if (parentOperator === 'AT_LEAST') {
      return renderAtLeastItem({
        queryItem,
        index,
        colorGroup,
        parentOperator,
        maxLength,
        atLeast,
        itemLabel,
      });
    }

    return renderRegularItem({
      queryItem,
      index,
      colorGroup,
      parentOperator,
      itemLabel,
    });
  };
};

const QueryPreviewContent = ({
  layout = 'vertical',
  type,
  query,
  queryEntitiesInfos,
  conversationQueries,
}) => {
  const getList = React.useMemo(() => createGetList(layout), [layout]);

  return (
    <>
      {getList(
        type,
        query,
        0,
        query,
        queryEntitiesInfos,
        conversationQueries,
        query.arguments_list.length
      )}
    </>
  );
};

export default QueryPreviewContent;
